import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import { Ornament } from "@/components/Ornaments";
import { emailError } from "@/lib/validate";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    const em = emailError(email, { required: true });
    setErr(em);
    if (em) return;
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name || u.email}`);
      const home = ["super_admin","major_admin","admin"].includes(u.role) ? "/admin" : (u.role === "venue_owner" || u.role === "staff") ? "/owner" : "/account";
      const from = loc.state?.from;
      // never send someone to another role's dashboard (e.g. left over from a previous user's session)
      nav(from && !["/owner", "/admin", "/account", "/login", "/register"].includes(from) ? from : home);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Login failed"); } finally { setBusy(false); }
  };

  return (
    <AuthShell image="https://images.unsplash.com/photo-1616668856493-9df876327739?w=1400" kicker="Welcome back" title="Your next celebration awaits." side="right">
      <form onSubmit={submit} noValidate className="space-y-5" data-testid="login-form">
        <div>
          <img src="/logo.png" alt="DartMyVenue" className="h-12 w-auto mb-5"/>
          <h1 className="font-display text-4xl md:text-5xl text-[color:var(--maroon)]">Sign in</h1>
          <p className="text-sm text-neutral-600 mt-2">New to DartMyVenue? <Link className="text-[color:var(--maroon)] font-semibold underline underline-offset-4" to="/register">Create an account</Link></p>
        </div>
        <Field icon={Mail} label="Email" error={err}><input type="email" inputMode="email" autoComplete="email" required placeholder="you@example.com" value={email} onChange={e=>{ setEmail(e.target.value); if (err) setErr(""); }} onBlur={()=>email && setErr(emailError(email))} className={`input !pl-11 ${err ? "invalid" : ""}`} aria-invalid={!!err} data-testid="login-email"/></Field>
        <Field icon={Lock} label="Password">
          <input type={show ? "text" : "password"} required placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)} className="input !pl-11 !pr-11" data-testid="login-password"/>
          <button type="button" onClick={()=>setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-[color:var(--maroon)]" aria-label="Toggle password">{show ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}</button>
        </Field>
        <div className="flex items-center justify-between text-xs">
          <label className="inline-flex items-center gap-2 text-neutral-600"><input type="checkbox" className="accent-[color:var(--maroon)]"/> Keep me signed in</label>
          <Link to="/forgot-password" className="text-[color:var(--maroon)] font-semibold hover:underline">Forgot password?</Link>
        </div>
        <button disabled={busy} className="btn-wine w-full !py-4 text-base" data-testid="login-submit">{busy ? "Signing in…" : <>Sign in <ArrowRight className="w-4 h-4"/></>}</button>
</form>
    </AuthShell>
  );
}

export function Field({ icon: I, label, error, children }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <span className="relative block">
        <I className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--gold)] z-10"/>
        {children}
      </span>
      {error && <span className="field-error" role="alert">{error}</span>}
    </label>
  );
}

export function AuthShell({ image, kicker, title, side = "right", children }) {
  const pic = (
    <div className="hidden lg:block relative lg:sticky lg:top-[72px] h-[calc(100vh-72px)] min-h-[560px]">
      <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover"/>
      <div className="absolute inset-0 hero-overlay"/>
      <div className="absolute inset-0 border-[14px] border-[color:var(--cream-primary)]/0"/>
      <div className="absolute bottom-12 left-12 right-12 text-white">
        <Ornament className="w-20 h-4 ornament"/>
        <div className="text-[color:var(--gold-2)] font-semibold mt-4">{kicker}</div>
        <div className="font-display text-5xl mt-2 leading-tight">{title}</div>
        <div className="mt-6 text-[15px] text-white/75">Zero commission · Talk to owners directly</div>
      </div>
    </div>
  );
  return (
    <div className="min-h-[88vh] grid lg:grid-cols-2 jali items-start">
      {side === "left" && pic}
      {/* top-aligned so the card never jumps when its content grows (e.g. picking "I own a business") */}
      <div className="flex justify-center px-4 sm:px-10 py-8 sm:py-12 md:py-16">
        <div className="w-full max-w-[480px] card-heritage p-6 sm:p-10">{children}</div>
      </div>
      {side === "right" && pic}
    </div>
  );
}
